import { FUSIONNOW_BASE_URL, getFusionNowToken, getMacMarketplaceToken } from './fusionNowAuth';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WorkflowTouchpointEmailAttr {
  subject: string;
  body: string;
  name?: string;
  email_template_id: number;
  email_template_key: string;
}

export interface WorkflowTouchpointCustomAttr {
  // nowcerts_task
  category_name?: string;
  descriptive_time?: string;
  assigne_type?: string;
  // opportunity_stage / create_opportunity
  stage?: string;
  pipeline_kanban_id?: number;
  win_probability?: number;
  // add_tag / remove_tag
  tag_type?: string;
  // insured_note
  note_type?: string;
  subject?: string;
  target_entity_type?: string;
  // webhook_trigger
  webhook_url?: string;
  webhook_action_type?: string;
  // clone_policy_as_quote
  billing_type?: string;
  copy_selected_items?: string[];
  // loss_run
  reminder_days?: number;
  request_type?: string;
  email_template?: { id: number; name: string };
  nowcerts_carrier_mga?: { id: number; insurance_company: string };
  // agent_assignment (names not displayed — IDs only)
  agent_ids?: number[];
  csr_ids?: number[];
}

export interface WorkflowTouchpoint {
  id: number;
  type: string;
  delay: number;
  emails_attributes?: WorkflowTouchpointEmailAttr[];
  content?: string;
  custom_task_attributes?: WorkflowTouchpointCustomAttr;
}

export interface WorkflowTrigger {
  id: number;
  filter: string;
  filter_value: string;
  filter_date: string;
  filter_condition: {
    id: number;
    name: string;
    trigger_category: {
      id: number;
      name: string;
      category_type?: string;
    };
  } | null;
}

export interface WorkflowUser {
  id: number;
  name: string;
  avatar: { url: string };
}

export interface Workflow {
  id: number;
  key: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'stopped' | string;
  running_state: string | null;
  wf_type: string;
  workflow_contacts_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  replied_count: number;
  created_at: string;
  updated_at: string;
  touchpoints: WorkflowTouchpoint[];
  workflow_trigger: WorkflowTrigger | null;
  user: WorkflowUser;
  is_recipe: boolean;
  shared: boolean;
}

export interface WorkflowMeta {
  current_page: number;
  next_page: number | null;
  prev_page: number | null;
  total_pages: number;
  total_count: number;
}

export interface WorkflowsResponse {
  workflows: Workflow[];
  meta: WorkflowMeta;
}

// Public workflow has a string id; use workflow.key for all clone API calls
export type PublicWorkflow = Omit<Workflow, 'id'> & { id: string };

export interface PublicWorkflowsResponse {
  workflows: PublicWorkflow[];
  meta: WorkflowMeta;
}

export interface ClonePreviewData {
  workflow_name: string;
  has_trigger: boolean;
  touchpoint_count: number;
  missing_entities: string[];
  warnings: string[];
  summary: { total_entities: number; matched: number; missing: number };
  workflow: null;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function getWorkflows(
  page = 1,
  search = '',
  perPage = 12
): Promise<WorkflowsResponse> {
  const token = await getFusionNowToken();

  const searchParam = search.trim()
    ? `&search=${encodeURIComponent(search.trim())}`
    : '';
  const url = `${FUSIONNOW_BASE_URL}/api/open_api/v1/workflows?per_page=${perPage}&page=${page}&list_type=all&categorized_data=true${searchParam}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok) throw new Error(`Failed to load workflows: ${res.status}`);

  const data = await res.json();
  if (!data.success) throw new Error('Workflows API returned success: false');

  return {
    workflows: data.data as Workflow[],
    meta: data.meta_attributes as WorkflowMeta
  };
}

export async function getPublicWorkflows(
  page = 1,
  search = '',
  perPage = 12
): Promise<PublicWorkflowsResponse> {
  const token = await getMacMarketplaceToken();

  const searchParam = search.trim()
    ? `&keyword=${encodeURIComponent(search.trim())}`
    : '';
  const url = `${FUSIONNOW_BASE_URL}/api/marketplace/public_workflows?per_page=${perPage}&page=${page}${searchParam}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok)
    throw new Error(`Failed to load public workflows: ${res.status}`);

  const data = await res.json();
  if (!data.success)
    throw new Error('Public workflows API returned success: false');

  return {
    workflows: data.data as PublicWorkflow[],
    meta: data.meta_attributes as WorkflowMeta
  };
}

export async function crossAgencyCloneWorkflow(
  id: string
): Promise<{ success: boolean }> {
  const token = await getFusionNowToken();

  const res = await fetch(
    `${FUSIONNOW_BASE_URL}/api/open_api/v1/workflows/${id}/cross_agency_clone`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!res.ok) throw new Error(`Clone failed: ${res.status}`);

  const data = await res.json();
  return { success: data.success ?? true };
}

export async function clonePreviewWorkflow(
  key: string
): Promise<ClonePreviewData> {
  const token = await getFusionNowToken();

  const res = await fetch(
    `${FUSIONNOW_BASE_URL}/api/open_api/v1/workflows/${key}/clone_preview`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!res.ok) throw new Error(`Clone preview failed: ${res.status}`);

  const data = await res.json();
  if (!data.success) throw new Error('Clone preview returned success: false');

  return data.data as ClonePreviewData;
}

export async function getMacSsoToken(amsToken: string): Promise<string> {
  const res = await fetch(
    `${process.env.REACT_APP_Auth_Url}/api/account/macssotoken?nctoken=${encodeURIComponent(amsToken)}`
  );

  if (!res.ok) throw new Error(`SSO token request failed: ${res.status}`);

  // API may return plain text token or JSON { token: '...' }
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return typeof data === 'string' ? data : (data.token as string);
  } catch {
    return text.trim();
  }
}

export async function copyWorkflow(id: number): Promise<{ success: boolean }> {
  const token = await getFusionNowToken();

  const res = await fetch(`${FUSIONNOW_BASE_URL}/api/workflows/${id}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!res.ok) throw new Error(`Copy failed: ${res.status}`);

  const data = await res.json();
  return { success: data.success ?? true };
}
